import React, { useEffect, useRef, useState } from 'react';

import { Photo } from '@travel/icons/ui';
import { isNotEmptyArray } from '@travel/utils';

import VideoPlayer from '../VideoPlayer';

import cx from '../../utils/classnames';

import styles from './media.module.scss';

import type { VideoJsPlayer } from 'video.js';
export type Source = {
  /** String of MIME type */
  mimeType?: string;
  /** String of media query */
  mediaQuery?: string;
  /** String of possible images represented by the source for the browser */
  srcset?: string;
  /** String of list of source sizes that describes the final rendered width of the image represented by the source */
  sizes?: string;
};

export type Props = {
  /** Custom style for media */
  className?: string;

  /** Test id for the component */
  'data-testid'?: string;

  /** Custom style for wrapper */
  wrapperClassName?: string;

  /** Media type */
  type?: string;

  /** String of media id */
  id?: string;

  /** Flag if text has been translated by Google */
  isTextsGoogleTranslated?: boolean;

  /**
   * String of media url. The url can be for image or video.
   * if both video id and url are set, we will use the url instead
   * */
  url?: string;

  /** VPF Reference ID */
  refId?: string;

  /** VPF Service ID */
  serviceId?: string;

  /** Video id */
  videoId?: string;

  /** String of video id */
  videoVendorId?: string;

  /** Video controls */
  hasControls?: boolean;

  /** Video width */
  width?: number;

  /** Video height */
  height?: number;

  /** Enable ResizeObserver for better dimension detection (default: false) */
  useResizeObserver?: boolean;

  /** String of thumbnail video url */
  thumbnailUrl?: string;

  /** String of media alt */
  alt?: string;

  /** Array of image source options */
  imageSources?: Array<Source>;

  /** Flag video is playable or not */
  isPlayable?: boolean;

  /** String to define loading attr on Image */
  loading?: 'eager' | 'lazy';

  /** React node to define custom placeholder  */
  placeholder?: React.ReactNode;

  /** Component ref  */
  innerRef?: React.Ref<HTMLDivElement>;

  /** Callback to be called when the component is clicked  */
  onClick?: (event: React.MouseEvent) => void;

  tabIndex?: number;

  /** Callback to be called when the component is clicked  */
  onKeyDown?: (event: React.KeyboardEvent) => void;

  /** Callback to be called image has failed to load  */
  onImageError?: () => void;

  /** Experimental img attribute https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/fetchPriority */
  fetchPriority?: 'high' | 'low' | 'auto';

  /** Should not return a div only the media component */
  isMediaOnly?: boolean;

  /** Should Pause Video or not */
  shouldPauseVideo?: boolean;

  /** Video Order Number for unique identification for the VPF playback script */
  orderNumber?: number;
} & Omit<Source, 'mimeType' | 'mediaQuery'>;

function MediaPlaceholder(props: {
  className?: string;
  placeholder?: React.ReactNode;
  testId?: string;
}) {
  return (
    <div className={cx(props.className, styles.empty)} data-testid={props.testId}>
      {props.placeholder || <Photo className={styles.icon} size={24} />}
    </div>
  );
}

const DEFAULT_SERVICE_ID = '1200000001';

function Video(props: Props) {
  const [isError, setIsError] = useState(false);
  const videoJsPlayer = useRef<VideoJsPlayer | null>(null);

  useEffect(() => {
    if (videoJsPlayer.current && props.shouldPauseVideo) {
      videoJsPlayer.current.pause();
    }
  }, [props.shouldPauseVideo]);

  const handleError = () => {
    setIsError(true);
  };

  if (props.refId && !isError) {
    return (
      <VideoPlayer
        refId={props.refId}
        serviceId={props.serviceId || DEFAULT_SERVICE_ID}
        videoId={props.videoId}
        height={props.height}
        width={props.width}
        useResizeObserver={props.useResizeObserver}
        autoplay={!props.shouldPauseVideo}
        hasControls={props.hasControls}
        className={props.className}
        onError={handleError}
        orderNumber={props.orderNumber}
      />
    );
  }

  return (
    <MediaPlaceholder
      className={props.className}
      placeholder={props.placeholder}
      testId={props['data-testid']}
    />
  );
}

function Image(props: Props) {
  const img = (
    <img
      data-testid={props['data-testid']}
      className={cx(props.className, styles.media)}
      src={props.url}
      alt={props.alt}
      srcSet={props.srcset}
      sizes={props.sizes}
      loading={props.loading}
      onError={props.onImageError}
      {...(props.fetchPriority ? { fetchpriority: props.fetchPriority } : {})}
    />
  );

  if (!isNotEmptyArray(props.imageSources)) return img;

  return (
    <picture>
      {props.imageSources.map((source) => (
        <source
          data-testid={props['data-testid']}
          srcSet={source.srcset}
          sizes={source.sizes}
          media={source.mediaQuery}
          type={source.mimeType}
        />
      ))}
      {img}
    </picture>
  );
}

function Media(props: Props) {
  const {
    id,
    className,
    wrapperClassName,
    url,
    thumbnailUrl,
    alt,
    type,
    isPlayable,
    fetchPriority,
    'data-testid': dataTestId,
    isTextsGoogleTranslated,
    onClick,
    onKeyDown,
    onImageError,
    placeholder,
    loading,
    innerRef,
    shouldPauseVideo,
    srcset,
    sizes,
    // video
    videoId,
    videoVendorId,
    isMediaOnly,
    orderNumber,
    ...rest
  } = props;

  let component = (
    <MediaPlaceholder testId={dataTestId} className={className} placeholder={placeholder} />
  );

  if (url && type?.toLowerCase() === 'image') {
    // image tye
    component = <Image {...props} {...(isMediaOnly ? { ref: innerRef } : {})} />;
  } else if (url || videoVendorId || videoId) {
    // case: only video id exist
    if (!url) {
      if (isPlayable) component = <Video {...props} />;
      else
        component = (
          <Image {...props} url={props.thumbnailUrl} {...(isMediaOnly ? { ref: innerRef } : {})} />
        );
    } else {
      // case: only url / both video, url exist
      component = <video className={cx(className, styles.media)} src={url} controls={isPlayable} />;
    }
  }
  if (isMediaOnly) {
    return component;
  }

  return (
    <div
      ref={innerRef}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={wrapperClassName}
      id={id}
      {...rest}
    >
      {component}
    </div>
  );
}

Media.defaultProps = {
  className: '',
  type: 'image',
  url: '',
  alt: '',
  isPlayable: false,
  onClick: () => {},
  id: '',
};

export const MediaWithRef = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => (
  <Media {...props} innerRef={ref} />
));

export default Media;
